import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";

import defaultStyles from "./EpubStyles";
import {
  BOOK_FIRST_PAGE,
  BOOK_LAST_PAGE,
  BOOK_NEXT_PAGE,
  BOOK_PREV_PAGE,
} from "./events";
import {
  nextPage,
  prevPage,
  setTotalPage,
  updateCurrentPage,
} from "../../actions/bookActions";
import { updatePagesVisible } from "../../actions/bookViewActions";
import * as types from "../../actions/actionTypes";
import constants from "../../constants/constants";
import { studentReadingEventTypes } from "../../constants/enums";
import BookAPI from "../../api/bookAPI";
import Loading from "../common/Loading";
import icon_cross from "../../images/cross.svg";
import icon_arrow from "../../images/icon_arrow.svg";
import icon_play from "../../images/player_play.svg";
import icon_pause from "../../images/player_pause.svg";
import icon_zoom_in from "../../images/zoom_in.svg";
import icon_zoom_out from "../../images/zoom_out.svg";
import icon_onepage from "../../images/file.svg";
import icon_twopage from "../../images/pages.svg";

// foliate-js is served as static, runtime-loaded ESM from /foliate-js/ (NOT webpack-bundled).
// This avoids CRA 4 / webpack 4 choking on foliate's dynamic imports (pdfjs, zip workers, mobi).
// `nativeImport` uses `new Function` so webpack's static analyzer leaves the import untouched and
// the browser performs a true native dynamic import — exactly how the foliate spike loaded it.
const FOLIATE_BASE = `${process.env.PUBLIC_URL || ""}/foliate-js`;
const nativeImport = (url) => new Function("u", "return import(u)")(url);

const SILENT_HOLD_MS = 2000;
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

// ---------- look-ahead prefetch ----------
// foliate's fixed-layout renderer cold-fetches each page's HTML + full-page image on every turn
// and revokes the previous page, so turning feels slow (the old ReadiumSDK kept the spine warm).
// We pre-warm the next few pages into an IN-MEMORY map keyed by the same item.href foliate uses,
// so the next turn is a cache hit. Two hard constraints for security audits (e.g. PRH):
//   1. every fetch is `cache: "no-store"` -> book bytes are NEVER written to the browser's
//      on-disk HTTP cache; they exist only in the JS heap (gone when the tab/reader closes).
//   2. the cache is a bounded sliding window: entries outside [current-BEHIND, current+AHEAD] are
//      evicted on each page turn, there is a hard max-age backstop, and everything is purged on
//      unmount. So a page only lingers in memory while the reader is near it.
const PREFETCH_AHEAD = 3; // pages to pre-load ahead of the current one
const PREFETCH_BEHIND = 1; // pages behind (cheap; also fixes back-nav re-fetch)
// Backstop for a tab left open + idle: drop entries older than this even if still in-window.
// 1 week suits a school session (a kid parked on a page won't hit a re-fetch when they resume).
// This does NOT weaken the no-linger story: the cache is in-memory only (gone on tab close) and
// is still window-evicted on every page turn; nothing is ever written to disk (cache:"no-store").
const PREFETCH_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 1 week
const XLINK_NS = "http://www.w3.org/1999/xlink";

class EpubViewFoliate extends Component {
  static defaultProps = {
    loadingView: null,
    styles: defaultStyles,
  };

  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      playingMediaOverlay: false,
      mediaOverlayPlayable: false,
      currentSection: 0,
      totalSections: 1,
    };
    this.debouncedSetState = debounce(this.setState, 300).bind(this);
    this.theme = constants.themeProvider.activeTheme;

    this.view = null;
    this.book = null;
    this.mo = null; // media overlay
    this.zoom = 1;
    this.readAloudActive = false;
    this.started = false;
    this.paused = false;
    this.audioUnlocked = false;

    // read-aloud highlight repaint state (we render the highlight ourselves; foliate's FXL
    // highlight tags the wrong document — same fix proven in the spike)
    this.curId = null;
    this.lastHL = null;

    // look-ahead prefetch: in-memory only, sliding window, no disk cache (see notes at top)
    this._mem = new Map(); // name -> { text?, blob?, focus (section idx), ts }
    this._prefetched = new Set(); // section indices already warmed
    this._prefetchTimer = null;

    this.stageRef = React.createRef();
  }

  componentDidMount() {
    this.setEventHandler();
    this.initBook();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.url !== this.props.url) this.initBook();
  }

  componentWillUnmount() {
    this.removeEventHandler();
    try {
      this.mo?.pause?.();
    } catch (e) {}
    clearInterval(this.repaintTimer);
    clearInterval(this.pillTimer);
    window.removeEventListener("resize", this.positionPills);
    // release prefetched book bytes + foliate's own blob URLs so nothing lingers after close
    this.purgeCache();
    try {
      this.view?.renderer?.destroy?.();
      this.view?.close?.();
    } catch (e) {}
  }

  // ---------- foliate setup ----------
  async initBook() {
    if (!this.props.url) return;
    try {
      this.setState({ isLoaded: false });
      const origin = window.location.origin;
      await nativeImport(`${origin}${FOLIATE_BASE}/view.js`); // defines <foliate-view>
      const { EPUB } = await nativeImport(`${origin}${FOLIATE_BASE}/epub.js`);

      // HTTP loader over the EXPLODED epub directory (this is why the epubs were exploded —
      // only the files actually needed get fetched, no 13MB zip download).
      // Use the SAME-ORIGIN path (drop any absolute host/scheme baked into props.url, e.g.
      // https://localhost:3000 in dev) so fetches go through the current origin: the dev proxy
      // (setupProxy.js) locally and server.js's /books proxy in production.
      const base = new URL(this.props.url, window.location.origin).pathname.replace(
        /\/+$/,
        "",
      );
      const url = (name) => `${base}/${name.replace(/^\/+/, "")}`;

      // All book bytes are fetched no-store (never persisted to the browser's on-disk HTTP cache)
      // and held in this._mem only. `focus` tags the entry with the section it belongs to so the
      // sliding-window eviction can drop it once the reader moves away. Both foliate's own loads
      // and our prefetch go through these two functions, so the keys (item.href) always match.
      const rawFetch = (name) =>
        fetch(url(name), { cache: "no-store", credentials: "omit" });
      const store = (name, patch, focus) => {
        const cur = this._mem.get(name) || {};
        const f = focus != null ? focus : this.state.currentSection;
        this._mem.set(name, { ...cur, ...patch, focus: f, ts: Date.now() });
      };
      // focus is explicit during prefetch; undefined for foliate's own (live) loads -> current page
      this._fetchText = async (name, focus) => {
        const hit = this._mem.get(name);
        if (hit && hit.text != null) {
          store(name, {}, focus);
          return hit.text;
        }
        const r = await rawFetch(name);
        if (!r.ok) return null;
        const text = await r.text();
        store(name, { text }, focus);
        return text;
      };
      this._fetchBlob = async (name, focus) => {
        const hit = this._mem.get(name);
        if (hit && hit.blob != null) {
          store(name, {}, focus);
          return hit.blob;
        }
        const r = await rawFetch(name);
        if (!r.ok) return null;
        const blob = await r.blob();
        store(name, { blob }, focus);
        return blob;
      };
      this._urlOf = url;
      const loader = {
        loadText: (name) => this._fetchText(name),
        loadBlob: (name) => this._fetchBlob(name),
        // Progress sizing isn't needed (our slider is section-index based); keep it cheap.
        getSize: () => 0,
      };

      const book = await new EPUB(loader).init();
      this.book = book;

      const view = document.createElement("foliate-view");
      // <foliate-view> is a custom element; without explicit display it is inline, which (a)
      // makes CSS `transform: scale()` (our zoom) a no-op and (b) breaks sizing/arrow alignment.
      view.style.display = "block";
      view.style.width = "100%";
      view.style.height = "100%";
      view.style.background = "#f5f5f5";
      const stage = this.stageRef.current;
      if (!stage) return;
      stage.innerHTML = "";
      stage.append(view);
      this.view = view;

      await view.open(book);
      this.mo = view.mediaOverlay;

      const total = book.sections.length;
      this.setState({ totalSections: total });
      this.props.setTotalPage(total);

      // Wire handlers BEFORE the first render so the first relocate event is captured.
      this.wireRelocate();
      this.wireReadAloud();
      this.startHighlightLoop();

      window.addEventListener("resize", this.positionPills);
      this.pillTimer = setInterval(this.positionPills, 1000);

      // AWAIT the first page: renderer.next() resolves only after the section HTML + image have
      // loaded and the iframe has painted. Hiding "Loading" before this (the old un-awaited call)
      // is exactly what caused the blank gap between the spinner disappearing and the page showing.
      await view.renderer.next();
      setTimeout(this.positionPills, 200);

      this.setState({ isLoaded: true });
      this.schedulePrefetch(this.view?.renderer?.index ?? 0);
    } catch (e) {
      console.error("[EpubViewFoliate] open failed:", e);
      this.setState({ isLoaded: true });
    }
  }

  wireRelocate() {
    this.view.addEventListener("relocate", () => {
      const idx = this.view.renderer.index;
      if (typeof idx === "number") {
        this.setState({ currentSection: idx });
        this.props.updateCurrentPage(idx);
        this.props.updatePage(idx);
        this.schedulePrefetch(idx);
      }
      this.debouncedSetState({
        mediaOverlayPlayable: !!this.mo,
      });
      setTimeout(this.positionPills, 30);
    });
  }

  // ---------- look-ahead prefetch ----------
  // Debounced so rapid page turns don't thrash; warms the window, then evicts the rest.
  schedulePrefetch = (center) => {
    clearTimeout(this._prefetchTimer);
    this._prefetchTimer = setTimeout(() => this.prefetchAround(center), 150);
  };

  prefetchAround = (center) => {
    if (!this.book || !this.book.sections) return;
    const n = this.book.sections.length;
    const lo = Math.max(0, center - PREFETCH_BEHIND);
    const hi = Math.min(n - 1, center + PREFETCH_AHEAD);
    for (let i = lo; i <= hi; i++) {
      if (i === center) continue; // current page is already loaded by foliate
      this.prefetchSection(i); // fire-and-forget; best-effort
    }
    this.evictOutsideWindow(center);
  };

  // Warm one section's HTML + its images into the in-memory cache, tagged to that section index.
  prefetchSection = async (idx) => {
    if (this._prefetched.has(idx)) return;
    const sec = this.book.sections[idx];
    if (!sec || !sec.id || !this._fetchText) return;
    this._prefetched.add(idx);
    try {
      const name = sec.id.replace(/^\/+/, "");
      const html = await this._fetchText(name, idx);
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const refs = [];
      doc.querySelectorAll("img[src]").forEach((el) =>
        refs.push(el.getAttribute("src")),
      );
      doc.querySelectorAll("image").forEach((el) => {
        const h =
          el.getAttribute("href") || el.getAttributeNS(XLINK_NS, "href");
        if (h) refs.push(h);
      });
      for (const ref of refs) {
        if (!ref || /^(data|blob|https?):/i.test(ref)) continue;
        let resolved;
        try {
          resolved = sec.resolveHref ? sec.resolveHref(ref) : null;
        } catch (e) {
          resolved = null;
        }
        if (!resolved) continue;
        this._fetchBlob(String(resolved).replace(/^\/+/, ""), idx); // fire-and-forget
      }
    } catch (e) {
      // prefetch is purely an optimization — never let it break the viewer
      this._prefetched.delete(idx);
    }
  };

  // Keep only the sliding window; drop everything else (and anything past the max-age backstop).
  evictOutsideWindow = (center) => {
    const lo = center - PREFETCH_BEHIND;
    const hi = center + PREFETCH_AHEAD;
    const now = Date.now();
    for (const [name, e] of this._mem) {
      const stale = e.ts != null && now - e.ts > PREFETCH_MAX_AGE_MS;
      const outside = e.focus == null || e.focus < lo || e.focus > hi;
      if (stale || outside) {
        this._mem.delete(name); // drops our Blob/text ref -> GC; foliate revokes its own URLs
        this._prefetched.delete(e.focus);
      }
    }
  };

  // Release every cached page (called on unmount). Book content must not outlive the reader.
  purgeCache = () => {
    clearTimeout(this._prefetchTimer);
    this._mem.clear();
    this._prefetched.clear();
  };

  // ---------- read-aloud (ported from spike) ----------
  wireReadAloud() {
    const { book } = this;
    const mo = this.mo;
    if (mo) {
      const origStart = mo.start.bind(mo);
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      // Silent pages: foliate skips audio-less pages instantly. Show the page, hold ~2s,
      // then advance — wrapping start() also paces the end-of-page auto-advance chain.
      mo.start = async (sectionIndex, filter) => {
        const sec = book.sections[sectionIndex];
        if (!sec) {
          this.readAloudActive = false;
          this.setPlaying(false);
          return;
        }
        if (!sec.mediaOverlay) {
          try {
            await this.view.renderer.goTo({
              index: sectionIndex,
              anchor: () => 0,
            });
          } catch (e) {}
          await sleep(SILENT_HOLD_MS);
          if (!this.readAloudActive) return;
          return mo.start(sectionIndex + 1, filter);
        }
        return origStart(sectionIndex, filter);
      };

      mo.addEventListener("highlight", (e) => {
        const t = e.detail?.text || "";
        this.curId = t.split("#")[1] || null;
      });
    }
    this.setState({ mediaOverlayPlayable: !!mo });
  }

  startHighlightLoop() {
    const clearHL = () => {
      if (this.lastHL) {
        this.lastHL.style.background = "";
        this.lastHL.style.boxShadow = "";
        this.lastHL.style.borderRadius = "";
        this.lastHL = null;
      }
    };
    this.repaintTimer = setInterval(() => {
      if (!this.curId) {
        clearHL();
        return;
      }
      for (const c of this.view.renderer.getContents()) {
        const el = c.doc && c.doc.getElementById(this.curId);
        if (el) {
          if (el !== this.lastHL) {
            clearHL();
            el.style.background = "#ffe066";
            el.style.boxShadow = "0 0 0 4px #ffe066";
            el.style.borderRadius = "4px";
            this.lastHL = el;
          }
          return;
        }
      }
    }, 80);
  }

  unlockAudio() {
    if (this.audioUnlocked) return;
    try {
      const a = new Audio(SILENT_WAV);
      a.volume = 0;
      const p = a.play();
      if (p) p.then(() => (this.audioUnlocked = true)).catch(() => {});
    } catch (e) {}
  }

  setPlaying(playing) {
    this.setState({ playingMediaOverlay: playing });
  }

  // Header Play/Pause routes here (status=true play, false pause)
  updateMediaOverlayPlaying = (status) => {
    if (!this.mo) {
      this.setPlaying(false);
      return;
    }
    if (status) {
      this.unlockAudio();
      this.readAloudActive = true;
      if (this.started && this.paused) {
        try {
          this.mo.resume();
        } catch (e) {}
        this.paused = false;
      } else {
        let idx = this.view.renderer.index;
        if (typeof idx !== "number")
          idx = this.book.sections.findIndex((s) => s.mediaOverlay);
        this.mo.start(idx);
        this.started = true;
        this.paused = false;
      }
      this.setPlaying(true);
    } else {
      this.readAloudActive = false;
      this.paused = true;
      try {
        this.mo.pause();
      } catch (e) {}
      this.setPlaying(false);
    }
  };

  // ---------- page navigation ----------
  logEvent = async (eventType) => {
    try {
      await BookAPI.logEvent(this.props.book.ID, eventType, this.props.user);
    } catch (e) {
      console.error("[EpubViewFoliate] logEvent:", e);
    }
  };

  manualNav = async (dir) => {
    if (!this.view) return;
    await this.logEvent(
      dir > 0
        ? studentReadingEventTypes.PageNext
        : studentReadingEventTypes.PageBack,
    );
    if (dir > 0) await this.view.goRight();
    else await this.view.goLeft();
    if (this.readAloudActive && this.mo) this.mo.start(this.view.renderer.index);
  };

  nextPage = () => this.manualNav(1);
  prevPage = () => this.manualNav(-1);

  firstPage = () => {
    this.seekTo(0);
  };
  lastPage = () => {
    this.seekTo(this.state.totalSections - 1);
  };

  seekTo = async (idx) => {
    if (!this.view) return;
    try {
      await this.view.renderer.goTo({ index: idx, anchor: () => 0 });
    } catch (e) {}
    if (this.readAloudActive && this.mo) this.mo.start(idx);
  };

  // ---------- one / two page ----------
  setPages = (single) => {
    try {
      this.view?.renderer?.setSinglePage(single);
      setTimeout(this.positionPills, 60);
    } catch (e) {}
  };
  updatePagesVisble = (event) => {
    const single =
      event?.detail &&
      typeof event.detail.pagesVisible === "number" &&
      event.detail.pagesVisible === 1;
    this.setPages(single);
  };

  // ---------- zoom (CSS scale on the book) ----------
  applyZoom = () => {
    if (!this.view) return;
    this.view.style.transformOrigin = "top center";
    this.view.style.transform = `scale(${this.zoom})`;
    const stage = this.stageRef.current;
    if (stage) stage.style.overflow = this.zoom > 1 ? "auto" : "hidden";
    setTimeout(this.positionPills, 60);
  };
  increaseBookZoom = () => {
    this.zoom = Math.min(3, this.zoom + 0.15);
    this.applyZoom();
  };
  decreaseBookZoom = () => {
    this.zoom = Math.max(0.5, this.zoom - 0.15);
    this.applyZoom();
  };

  // ---------- page-turn pills tracking the book edges ----------
  positionPills = () => {
    if (!this.view || !this.view.renderer) return;
    const prev = this.prevPillRef;
    const next = this.nextPillRef;
    if (!prev || !next) return;
    const frames = [...this.view.renderer.getContents()]
      .map((c) => c.doc && c.doc.defaultView && c.doc.defaultView.frameElement)
      .filter(Boolean);
    if (!frames.length) return;
    let left = Infinity,
      right = -Infinity,
      top = Infinity,
      bottom = -Infinity;
    for (const f of frames) {
      const r = f.getBoundingClientRect();
      if (r.width) {
        left = Math.min(left, r.left);
        right = Math.max(right, r.right);
        top = Math.min(top, r.top);
        bottom = Math.max(bottom, r.bottom);
      }
    }
    if (!isFinite(left)) return;
    const cy = (top + bottom) / 2;
    for (const p of [prev, next]) {
      p.style.top = cy + "px";
      p.style.transform = "translateY(-50%)";
    }
    prev.style.left = Math.max(0, left - 46) + "px";
    next.style.left = Math.min(window.innerWidth - 46, right) + "px";
    next.style.right = "auto";
  };

  // ---------- slider ----------
  onSliderInput = (e) => {
    const idx = parseInt(e.target.value, 10) - 1;
    this.setState({ currentSection: idx });
    if (!this.seekBusy) this.runSeek(idx);
    else this.seekPending = idx;
  };
  runSeek = async (idx) => {
    this.seekBusy = true;
    try {
      await this.view.renderer.goTo({ index: idx, anchor: () => 0 });
    } catch (e) {}
    this.seekBusy = false;
    if (this.seekPending != null) {
      const n = this.seekPending;
      this.seekPending = null;
      this.runSeek(n);
    }
  };
  onSliderChange = (e) => {
    const idx = parseInt(e.target.value, 10) - 1;
    if (this.readAloudActive && this.mo) this.mo.start(idx);
  };

  // ---------- events ----------
  setEventHandler() {
    document.addEventListener(BOOK_NEXT_PAGE, this.nextPage);
    document.addEventListener(BOOK_PREV_PAGE, this.prevPage);
    document.addEventListener(BOOK_FIRST_PAGE, this.firstPage);
    document.addEventListener(BOOK_LAST_PAGE, this.lastPage);
    document.addEventListener(types.UPDATE_PAGES_VISIBLE, this.updatePagesVisble);
    document.addEventListener(types.INCREASE_BOOK_ZOOM, this.increaseBookZoom);
    document.addEventListener(types.DECREASE_BOOK_ZOOM, this.decreaseBookZoom);
    document.addEventListener("keydown", this.onKeyDown);
  }
  removeEventHandler() {
    document.removeEventListener(BOOK_NEXT_PAGE, this.nextPage);
    document.removeEventListener(BOOK_PREV_PAGE, this.prevPage);
    document.removeEventListener(BOOK_FIRST_PAGE, this.firstPage);
    document.removeEventListener(BOOK_LAST_PAGE, this.lastPage);
    document.removeEventListener(
      types.UPDATE_PAGES_VISIBLE,
      this.updatePagesVisble,
    );
    document.removeEventListener(types.INCREASE_BOOK_ZOOM, this.increaseBookZoom);
    document.removeEventListener(types.DECREASE_BOOK_ZOOM, this.decreaseBookZoom);
    document.removeEventListener("keydown", this.onKeyDown);
  }

  onKeyDown = (e) => {
    const tag = e.target && e.target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable)
      return;
    if (e.key === "ArrowRight") this.manualNav(1);
    else if (e.key === "ArrowLeft") this.manualNav(-1);
  };

  // Edge page-turn pill, styled inline to match the spike (half-circle button, prev mirrored).
  renderPagePill(side) {
    const isPrev = side === "prev";
    return (
      <div
        ref={(el) => (isPrev ? (this.prevPillRef = el) : (this.nextPillRef = el))}
        style={{ position: "fixed", zIndex: 100, opacity: 0.7 }}
      >
        <button
          onClick={isPrev ? this.prevPage : this.nextPage}
          title={isPrev ? "Previous page" : "Next page"}
          style={{
            height: "40vh",
            maxHeight: 400,
            width: 46,
            border: "none",
            background: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 5px rgba(0,0,0,.25)",
            borderRadius: isPrev ? "100px 0 0 100px" : "0 100px 100px 0",
            padding: 0,
          }}
        >
          <img
            alt=""
            src={icon_arrow}
            style={{
              width: 30,
              height: 56,
              transform: isPrev ? "matrix(-1, 0, 0, 1, 0, 0)" : "none",
            }}
          />
        </button>
      </div>
    );
  }

  render() {
    const { currentSection, totalSections, playingMediaOverlay } = this.state;
    const tbBtn = {
      background: "transparent",
      border: "none",
      padding: "8px 7px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
    };
    const tbImg = { width: 22, height: 22, display: "block" };
    const divider = {
      width: 1,
      height: 26,
      background: "#b3b3b3",
      margin: "0 8px",
    };
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#f5f5f5",
          zIndex: 1,
        }}
      >
        {/* header bar — off-white, like the spike ($toolbarBg = #f5f5f5) */}
        <div
          style={{
            position: "relative",
            height: 55,
            background: "#f5f5f5",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* centered control pill */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              background: "#fff",
              borderRadius: 100,
              boxShadow: "0 0 20px rgba(0,0,0,.12)",
              padding: "2px 10px",
            }}
          >
            <button
              title="Play"
              style={{ ...tbBtn, opacity: playingMediaOverlay ? 0.3 : 1 }}
              disabled={playingMediaOverlay}
              onClick={() => this.updateMediaOverlayPlaying(true)}
            >
              <img alt="Play" src={icon_play} style={tbImg} />
            </button>
            <button
              title="Pause"
              style={{ ...tbBtn, opacity: playingMediaOverlay ? 1 : 0.3 }}
              disabled={!playingMediaOverlay}
              onClick={() => this.updateMediaOverlayPlaying(false)}
            >
              <img alt="Pause" src={icon_pause} style={tbImg} />
            </button>
            <span style={divider} />
            <button
              title="One page"
              style={tbBtn}
              onClick={() => this.setPages(true)}
            >
              <img alt="One page" src={icon_onepage} style={tbImg} />
            </button>
            <button
              title="Two pages"
              style={tbBtn}
              onClick={() => this.setPages(false)}
            >
              <img alt="Two pages" src={icon_twopage} style={tbImg} />
            </button>
            <span style={divider} />
            <button
              title="Zoom out"
              style={tbBtn}
              onClick={this.decreaseBookZoom}
            >
              <img alt="Zoom out" src={icon_zoom_out} style={tbImg} />
            </button>
            <button
              title="Zoom in"
              style={tbBtn}
              onClick={this.increaseBookZoom}
            >
              <img alt="Zoom in" src={icon_zoom_in} style={tbImg} />
            </button>
          </div>

          {/* rounded close 'X' top-right */}
          <button
            title="Close"
            onClick={() => {
              this.updateMediaOverlayPlaying(false);
              this.props.closeBookView();
            }}
            style={{
              position: "fixed",
              right: 10,
              top: 8,
              width: 40,
              height: 40,
              background: "#fff",
              border: 0,
              borderRadius: 500,
              boxShadow: "0 0 20px rgba(0,0,0,.12)",
              cursor: "pointer",
              zIndex: 110,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
          >
            <img alt="Close" src={icon_cross} style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <Loading show={!this.state.isLoaded} />

        {/* stage — reserves 72px at the bottom for the slider so the book never sits behind it */}
        <div
          ref={this.stageRef}
          id="foliate-stage"
          style={{
            position: "relative",
            width: "100vw",
            height: "calc(100vh - 55px - 72px)",
            background: "#f5f5f5",
            overflow: "hidden",
          }}
        />

        {/* edge page-turn pills (positioned to the book edges by positionPills).
            Styled inline to match the spike — half-circle buttons, prev arrow mirrored. */}
        {this.renderPagePill("prev")}
        {this.renderPagePill("next")}

        {/* page slider — centered pill at the bottom */}
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: 16,
            transform: "translateX(-50%)",
            zIndex: 105,
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fff",
            borderRadius: 100,
            boxShadow: "0 0 20px rgba(0,0,0,.12)",
            padding: "8px 18px",
          }}
        >
          <input
            type="range"
            min={1}
            max={totalSections}
            value={currentSection + 1}
            step={1}
            onInput={this.onSliderInput}
            onChange={this.onSliderChange}
            style={{ width: 320, maxWidth: "50vw", cursor: "pointer" }}
          />
          <span
            style={{
              fontSize: 12,
              color: "#555",
              minWidth: 64,
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            {currentSection + 1} / {totalSections}
          </span>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  book: state.book,
  user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
  nextPage: () => dispatch(nextPage()),
  prevPage: () => dispatch(prevPage()),
  updatePagesVisible: (pages) => dispatch(updatePagesVisible(pages)),
  setTotalPage: (totalPage) => dispatch(setTotalPage(totalPage)),
  updateCurrentPage: (pageNumber) => dispatch(updateCurrentPage(pageNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EpubViewFoliate);
